/**
 * The inside of the model menu: Auto marked Recommended, each maker as a
 * hairline group, models not open yet marked Soon, and Thinking effort pinned
 * under them. Shared by the rail's picker (ModelPicker) and the Workshop's
 * menu (WorkshopModelMenu); each owns its own popover, open state and what a
 * pick does, so this only draws the list and reports clicks.
 *
 * Rows reveal top to bottom on open, 30ms apart (the .co-notif-in fade).
 */
import { useMemo, type CSSProperties } from "react";
import { Check } from "lucide-react";
import type { ChatModel, ReasoningEffort } from "@/types/chat.type";
import { groupChatModelsByProvider } from "@/hooks/useChatModels";
import { REASONING_LABELS } from "@/app/routes/dashboard/components/agentChatUi";
import { EffortSlider } from "./EffortSlider";
import { ModelMark } from "./RailParts";

type Props = {
  models: ChatModel[];
  selectedModel: ChatModel | null;
  /** Called for an enabled row; the caller decides what a pick changes. */
  onPick: (model: ChatModel) => void;
  effort: ReasoningEffort;
  onEffortChange: (effort: ReasoningEffort) => void;
  /** Id of the "Thinking effort" label, which the slider points at. */
  effortLabelId: string;
};

const stagger = (i: number) => ({ "--i": Math.min(i, 14) }) as CSSProperties;

export function ModelMenuBody({ models, selectedModel, onPick, effort, onEffortChange, effortLabelId }: Props) {
  const sections = useMemo(() => groupChatModelsByProvider(models), [models]);
  const auto = sections.find((section) => section.id === "recommended");
  const vendors = sections.filter((section) => section.id !== "recommended");
  const efforts = selectedModel?.reasoningOptions ?? [];
  const effortLabel = REASONING_LABELS[effort] ?? effort;
  const autoCount = auto?.children.length ?? 0;
  const rowCount = autoCount + vendors.reduce((n, sec) => n + sec.children.length, 0);

  const row = (model: ChatModel, index: number, note?: string) => {
    const on = model.id === selectedModel?.id;
    return (
      <button
        key={model.id}
        type="button"
        aria-pressed={on}
        disabled={model.disabled}
        className="m7r-menu-row co-notif-in"
        style={stagger(index)}
        title={model.description || undefined}
        onClick={() => {
          if (!model.disabled) onPick(model);
        }}
      >
        <ModelMark modelId={model.id} />
        <span>{model.name}</span>
        {note ? <small>{note}</small> : null}
        {model.disabled ? (
          <em>Soon</em>
        ) : on ? (
          <Check className="m7r-menu-check" aria-hidden strokeWidth={2} />
        ) : null}
      </button>
    );
  };

  return (
    <>
      <div className="m7r-menu-models">
        {auto?.children.map((model, i) => row(model, i, "Recommended"))}
        {/* Each row carries its maker's mark, so the vendors are hairline
            groups rather than headings. */}
        {vendors.map((section, si) => {
          const start = autoCount + vendors.slice(0, si).reduce((n, sec) => n + sec.children.length, 0);
          return (
            <div key={section.id} role="group" aria-label={section.title} className="m7r-menu-vendor">
              {section.children.map((model, mi) => row(model, start + mi))}
            </div>
          );
        })}
      </div>
      {efforts.length > 0 ? (
        <div className="m7r-menu-effort co-notif-in" style={stagger(rowCount)}>
          <div className="m7r-menu-rule" role="separator" />
          <div className="m7r-menu-group" id={effortLabelId}>
            Thinking effort
            <span key={effortLabel} className="m7r-effort-value">{effortLabel}</span>
          </div>
          <EffortSlider
            options={efforts}
            value={effort}
            onChange={onEffortChange}
            labelledBy={effortLabelId}
            startDelay={120 + Math.min(rowCount, 14) * 30}
          />
        </div>
      ) : null}
    </>
  );
}
